import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getDocuments, updateDocumentStatus, ingestDocument, Document } from '../api/documents';
import PageContainer from '../components/PageContainer';

const DocumentList: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<string>(() => searchParams.get('status') || '');

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (filter) next.set('status', filter); else next.delete('status');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadDocuments = async () => {
    try {
      const docs = await getDocuments(filter || undefined);
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIngest = async (id: number) => {
    if (!window.confirm('Publish this document so the chatbot can use it?')) {
      return;
    }

    try {
      await ingestDocument(id);
      alert('Document published successfully.');
      loadDocuments();
    } catch (error: any) {
      alert('Failed to publish document: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await updateDocumentStatus(id, newStatus);
      loadDocuments();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge-success">Active</span>;
      case 'inactive':
        return <span className="badge-neutral">Inactive</span>;
      case 'ingestion_failed':
        return <span className="badge-error">Failed</span>;
      default:
        return <span className="badge-warning">Draft</span>;
    }
  };

  return (
    <PageContainer
      title="Documents"
      subtitle="Manage the content the chatbot uses to answer questions."
      actions={
        <Link to="/documents/create" className="btn-primary gap-1.5">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          Upload Knowledge Units
        </Link>
      }
    >
      <div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-base w-auto min-w-[160px]"
        >
          <option value="">All Documents</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="ingestion_failed">Failed</option>
        </select>
      </div>

      {loading ? (
        <div className="card p-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-primary-500">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Loading documents...
          </div>
        </div>
      ) : documents.length === 0 ? (
        <div className="card p-12 text-center">
          <svg className="w-12 h-12 mx-auto text-primary-300 mb-3" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12H9.75m3 0H9.75m0 0l3-3m-3 3l3 3M5.625 5.625H9.75m-4.125 0A2.625 2.625 0 003 8.25v10.5A2.625 2.625 0 005.625 21h12.75A2.625 2.625 0 0021 18.75V8.25a2.625 2.625 0 00-2.625-2.625H9.75" />
          </svg>
          <p className="text-sm text-primary-500 mb-4">No documents found</p>
          <Link to="/documents/create" className="btn-primary text-xs px-3 py-2">Create your first document</Link>
        </div>
      ) : (
        <div className="card divide-y divide-primary-100 overflow-hidden">
          {documents.map((doc) => (
            <div key={doc.id} className="px-5 py-4 hover:bg-primary-50/50 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <Link to={`/documents/${doc.id}`} className="text-sm font-semibold text-primary-800 truncate hover:text-primary-700 transition-colors">
                      {doc.title}
                    </Link>
                    {getStatusBadge(doc.status)}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-primary-500">
                    <span className="inline-flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
                      </svg>
                      {doc.language.toUpperCase()}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                      </svg>
                      {new Date(doc.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {(doc.status === 'draft' || doc.status === 'ingestion_failed') && (
                    <button onClick={() => handleIngest(doc.id)} className="btn-primary text-xs px-3 py-1.5">
                      Publish
                    </button>
                  )}
                  {doc.status === 'active' && (
                    <button onClick={() => handleStatusChange(doc.id, 'inactive')} className="btn-secondary text-xs px-3 py-1.5">
                      Deactivate
                    </button>
                  )}
                  {doc.status === 'inactive' && (
                    <button onClick={() => handleStatusChange(doc.id, 'active')} className="btn-primary text-xs px-3 py-1.5">
                      Activate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageContainer>
  );
};

export default DocumentList;
